import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import RangeInput from '@/components/functional/RangeInput/RangeInput.tsx';
import StepFormActions from '@/components/shared/StepFormActions/StepFormActions.tsx';
import StepFormHint from '@/components/ui/StepFormHint/StepFormHint.tsx';
import StepFormSubtitle from '@/components/ui/StepFormSubtitle/StepFormSubtitle.tsx';
import StepFormTextarea from '@/components/functional/StepFormTextarea/StepFormTextarea.tsx';
import StepFormTitle from '@/components/ui/StepFormTitle/StepFormTitle.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import type {
  RecommendationEngineData,
  RecommendationEngineSteps,
} from '@/pages/recommendationEngine/recommendationEngineContext.ts';

const CONFIDENCE_MIN = 1;
const CONFIDENCE_MAX = 5;
const PRIMARY_ASSUMPTION_MAX = 100;

const CONFIDENCE_TICKS = [1, 2, 3, 4, 5] as const;

type CommitmentFields = {
  primary_assumption: string;
};

const StepCommitmentCheckpoint: FC = () => {
  const { t } = useTranslation();
  const { previousStep, nextStep, setContextData, contextData } = useStepperContext<
    RecommendationEngineSteps,
    RecommendationEngineData
  >();
  const d = contextData as Partial<RecommendationEngineData> | null;

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<CommitmentFields>({
    mode: 'onChange',
    defaultValues: {
      primary_assumption: d?.primary_assumption ?? '',
    },
  });

  const assumption = watch('primary_assumption');
  const confidence = d?.commitment_confidence ?? 3;
  const trimmedAssumption = assumption.trim();
  const withinMaxLength = assumption.length <= PRIMARY_ASSUMPTION_MAX;
  const canContinue = isValid && withinMaxLength && trimmedAssumption.length > 0;

  const onSubmit = (data: CommitmentFields) => {
    setContextData((prev) => ({
      ...prev,
      commitment_confidence: confidence,
      primary_assumption: data.primary_assumption.trim(),
    }));
    nextStep();
  };

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full min-h-0" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2 shrink-0">
          <StepFormTitle>{t('recommendationEnginePage.commitment.title')}</StepFormTitle>
          <StepFormSubtitle>{t('recommendationEnginePage.commitment.subtitle')}</StepFormSubtitle>
        </div>

        <div className="flex flex-col gap-3">
          <p className="font-dm text-sm font-semibold text-black/85">
            {t('recommendationEnginePage.commitment.confidenceLabel')}
          </p>
          <RangeInput
            single
            min={CONFIDENCE_MIN}
            max={CONFIDENCE_MAX}
            step={1}
            value={confidence}
            color="primary"
            onChange={(value) =>
              setContextData((prev) => ({
                ...prev,
                commitment_confidence: value,
              }))
            }
          />
          <div className="flex flex-row justify-between px-0.5">
            {CONFIDENCE_TICKS.map((tick) => (
              <span
                key={tick}
                className={`font-dm text-sm tabular-nums ${
                  tick === confidence ? 'font-semibold text-black' : 'text-black/40'
                }`}
              >
                {tick}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <StepFormHint>{t('recommendationEnginePage.commitment.assumptionQuestion')}</StepFormHint>
          <StepFormTextarea
            placeholder={t('recommendationEnginePage.commitment.typeHere')}
            maxLength={PRIMARY_ASSUMPTION_MAX}
            {...register('primary_assumption', { required: true })}
          />
          <StepFormHint>{t('recommendationEnginePage.commitment.assumptionHint')}</StepFormHint>
        </div>

        <StepFormActions
          onBack={previousStep}
          continueDisabled={!canContinue}
          continueLabel={t('recommendationEnginePage.commitment.continueGovernance')}
        />
      </form>
    </BrandCardSideContent>
  );
};

export default StepCommitmentCheckpoint;
